import { ProductReview } from '@root/src/types';
import { useEffect, useState } from 'react';
import { getSiteReviews } from '@root/src/services/productsApi';

export type SiteReviewsState = {
  reviews: ProductReview[];
  loading: boolean;
  page: number;
  averageStarRating: number;
  totalReviewCount?: number;
  canLoadMore?: boolean;
  error?: string;
};

const PER_PAGE = 20;

export default function useSiteReviews(): [SiteReviewsState, () => void] {
  const [state, setState] = useState<SiteReviewsState>({
    reviews: [],
    loading: true,
    page: 1,
    averageStarRating: 5,
    totalReviewCount: 0,
    canLoadMore: false,
  });

  const loadMoreReviews = () => {
    setState((prevState: SiteReviewsState) => ({ ...prevState, page: prevState.page + 1 }));
  };

  useEffect(() => {
    let isMounted = true;

    const fetchSiteReviews = async () => {
      try {
        const r = await getSiteReviews(state.page, PER_PAGE);
        const { bottomLine, reviews } = r.siteReviews;

        if (isMounted) {
          setState((prevState) => ({
            ...prevState,
            reviews: [...prevState.reviews, ...reviews],
            loading: false,
            canLoadMore: reviews.length === PER_PAGE,
            averageStarRating: bottomLine.averageScore,
            totalReviewCount: bottomLine.totalReview,
          }));
        }
      } catch (e: any) {
        if (isMounted) {
          setState((prevState) => ({
            ...prevState,
            loading: false,
            error: e.message || 'An error occurred while fetching site reviews.',
          }));
        }
        console.error(e);
      }
    };

    fetchSiteReviews();

    return () => {
      isMounted = false;
    };
  }, [state.page]);

  return [state, loadMoreReviews];
}
